import { useQuery, UseQueryOptions, UseQueryReturnType } from '@tanstack/vue-query'
import { computed, MaybeRef, unref } from 'vue'
import { usePejerBase } from './stores/pejerData'
import { ApiUrls, EndpointResponse } from './types/base'
import { ExtractParams } from './types/HelperTypes'
import { DataState, ErrorObject } from './types/types'

export type usePejerQueryOptions<T extends ApiUrls<any>, TData, TQueryData> = UseQueryOptions<
  TData,
  ErrorObject,
  TQueryData
> & {
  apiUrl?: T
  pathArgs?: Record<ExtractParams<T['path']>, string | number>
  body?: Record<string, unknown | MaybeRef<unknown>>
  // beforeQuery?: () => Promise<void>
  ignoreAuthorization?: boolean
}

export type UsePejerQueryResult<TQueryData> = {
  dataState: DataState
} & UseQueryReturnType<TQueryData, ErrorObject>

export default function usePejerQuery<
  T extends ApiUrls<any>,
  TData = EndpointResponse<T>,
  TQueryData = TData,
>(
  apiUrl: T,
  options?: usePejerQueryOptions<T, TData, TQueryData>,
  extraOptions?: {
    ignoreAuthorization?: boolean
  }
): UsePejerQueryResult<TQueryData> {
  const pejer = usePejerBase()
  const enabled = computed(
    () =>
      (pejer.isPermitted(apiUrl ?? options.apiUrl) &&
        ('enabled' in options ? options.enabled : true)) as boolean
  )

  const query = useQuery({
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: async (a) => {
      // if ('queryFn' in options) {
      //   console.log('usePejerQuery - fetching', options)
      //   if (options?.beforeQuery)
      //     await options.beforeQuery()

      //   return options.queryFn
      // }
      try {
        const unreffedBody = {}
        for (const key in options?.body || {}) {
          if (options.body[key] === undefined) continue
          const unreffedValue = unref(options.body[key])
          if (unreffedValue === undefined) continue
          unreffedBody[key] = unreffedValue
        }
        const res = await pejer.fetch<T, TData>(apiUrl ?? options.apiUrl, {
          pathArgs: options?.pathArgs,
          body: options?.body ? unreffedBody : undefined,
          ignoreAuthorization: extraOptions?.ignoreAuthorization ?? options.ignoreAuthorization,
          abortSignal: a.signal,
        })
        if ('error' in res) throw res
        return res.data
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    ...options,
    enabled,
  })

  return {
    ...query,
    dataState: {
      isLoading: query.isLoading,
      isError: query.isError,
      isSuccess: query.isSuccess,
      error: query.error,
      url: options.apiUrl,
    },
  }
}
